import React from "react";

/**
 * Catches dynamic-import (React.lazy) load failures caused by stale
 * chunk hashes after a new deploy and triggers a single automatic
 * reload so the user gets the fresh index.html.
 *
 * Without this, a failed lazy chunk rejects inside <Suspense fallback={null}>
 * and the user sees a permanently blank page until they manually refresh.
 */

const RELOAD_FLAG = "sabaody:chunk-reload-attempt";

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg =
    (error as { message?: string })?.message ??
    String(error);
  const name = (error as { name?: string })?.name ?? "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

interface State {
  error: Error | null;
}

export default class ChunkReloadBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error)) {
      reloadOnce();
      return;
    }
    // Non-chunk error: log for observability
    // eslint-disable-next-line no-console
    console.error("[ChunkReloadBoundary]", error);
  }

  handleRetry = () => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* noop */
    }
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          role="alert"
          style={{
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            textAlign: "center",
            gap: "1rem",
          }}
        >
          <h1 style={{ fontSize: "1.25rem", fontWeight: 600 }}>
            Une erreur est survenue au chargement de la page
          </h1>
          <p style={{ color: "#475569", maxWidth: 420 }}>
            Merci d'actualiser la page. Si le problème persiste, revenez à
            l'accueil.
          </p>
          <div style={{ display: "flex", gap: ".75rem" }}>
            <button
              onClick={this.handleRetry}
              style={{
                padding: ".6rem 1.1rem",
                borderRadius: 12,
                background: "hsl(195 60% 28%)",
                color: "white",
                fontWeight: 600,
              }}
            >
              Actualiser
            </button>
            <a
              href="/"
              style={{
                padding: ".6rem 1.1rem",
                borderRadius: 12,
                background: "#e2e8f0",
                color: "#0f172a",
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Accueil
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Also catch unhandled promise rejections from dynamic imports that
// happen outside React's render path, plus Vite's own preload errors.
function reloadOnce() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
    // Allow one reload per 30s window to avoid loops if the chunk is truly gone.
    if (Date.now() - last > 30_000) {
      sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
      window.location.reload();
    }
  } catch {
    /* noop */
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    reloadOnce();
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      reloadOnce();
    }
  });
}
